import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuGlobe, LuChevronDown } from 'react-icons/lu';
import { Icon } from '../lib/icons';
import styles from './Navbar.module.css';

const languages = [
  { code: 'fr', label: 'Francais' },
  { code: 'en', label: 'English' },
];

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('lang', code);
    setLangOpen(false);
  };

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <>
      <nav className={`${styles.navbar} ${scrolled ? styles.scrolled : ''}`}>
        <div className={styles.navInner}>
          <Link to="/" className={styles.logo}>
            <img src="/logolarge.png" alt="OFMIG.SHOP" className={styles.logoImg} />
          </Link>

          <div className={styles.navLinks}>
            <button className={styles.navLink} onClick={() => scrollToSection('hero')}>{t('nav.home')}</button>
            <button className={styles.navLink} onClick={() => scrollToSection('pricing')}>{t('nav.pricing')}</button>
            <button className={styles.navLink} onClick={() => scrollToSection('faq')}>{t('nav.faq')}</button>
            <button className={styles.navLink} onClick={() => scrollToSection('contact')}>{t('nav.contact')}</button>
          </div>

          <div className={styles.navActions}>
            <div className={styles.langDropdown} ref={langRef}>
              <button className={styles.langButton} onClick={() => setLangOpen(!langOpen)}>
                <Icon icon={LuGlobe} size={12} strokeWidth={1.5} />
                {currentLang.code.toUpperCase()}
                <Icon icon={LuChevronDown} size={10} strokeWidth={2} />
              </button>
              {langOpen && (
                <div className={styles.langMenu}>
                  {languages.map((lang) => (
                    <button key={lang.code} className={`${styles.langOption} ${i18n.language === lang.code ? styles.active : ''}`} onClick={() => changeLanguage(lang.code)}>
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Link to="/checkout" className={styles.ctaButton}>{t('nav.order')}</Link>
          </div>

          <button className={`${styles.hamburger} ${mobileOpen ? styles.open : ''}`} onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      <div className={`${styles.mobileMenu} ${mobileOpen ? styles.open : ''}`}>
        <button className={styles.mobileNavLink} onClick={() => scrollToSection('hero')}>{t('nav.home')}</button>
        <button className={styles.mobileNavLink} onClick={() => scrollToSection('pricing')}>{t('nav.pricing')}</button>
        <button className={styles.mobileNavLink} onClick={() => scrollToSection('faq')}>{t('nav.faq')}</button>
        <button className={styles.mobileNavLink} onClick={() => scrollToSection('contact')}>{t('nav.contact')}</button>
        <div className={styles.mobileLangSection}>
          <div className={styles.mobileLangGrid}>
            {languages.map((lang) => (
              <button key={lang.code} className={`${styles.mobileLangBtn} ${i18n.language === lang.code ? styles.active : ''}`} onClick={() => changeLanguage(lang.code)}>
                {lang.code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <Link to="/checkout" className={styles.mobileCta} onClick={() => setMobileOpen(false)}>{t('nav.order')}</Link>
      </div>
    </>
  );
};

export default Navbar;
